import { storePosBooking, PosStorePayload } from "./posApi";
import {
  getOfflinePosQueue,
  removeOfflinePosSale,
  updateOfflinePosSale,
  QueuedPosSale,
} from "../lib/offlinePosQueue";
import { isBrowserOffline, shouldUseCachedData } from "../lib/networkStatus";

export interface OfflinePosSyncResult {
  synced: number;
  failed: number;
  remaining: number;
}

export function buildPosStorePayload(
  payload: PosStorePayload,
  transactionId?: string | null,
  uuid?: string | null,
): PosStorePayload {
  return {
    ...payload,
    transaction_id: transactionId ?? payload.transaction_id ?? null,
    uuid: uuid ?? payload.uuid ?? null,
  };
}

export async function syncOfflinePosQueue(): Promise<OfflinePosSyncResult> {
  if (isBrowserOffline()) {
    return {
      synced: 0,
      failed: 0,
      remaining: getOfflinePosQueue().length,
    };
  }

  const queue = getOfflinePosQueue();
  if (queue.length === 0) {
    return { synced: 0, failed: 0, remaining: 0 };
  }

  let synced = 0;
  let failed = 0;

  for (const sale of queue) {
    try {
      await storePosBooking(sale.payload);
      removeOfflinePosSale(sale.id);
      synced += 1;
    } catch (error: unknown) {
      failed += 1;
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ||
        (error instanceof Error ? error.message : "Sync failed");
      updateOfflinePosSale(sale.id, {
        retryCount: sale.retryCount + 1,
        lastError: message,
      });

      if (!shouldUseCachedData(error)) {
        // Stop on server-side errors — retry later manually or on next online event.
        break;
      }
    }
  }

  return {
    synced,
    failed,
    remaining: getOfflinePosQueue().length,
  };
}

export function createQueuedPosSaleInput(
  payload: PosStorePayload,
  amountPaid: number,
  changeDue: number,
): Omit<QueuedPosSale, "retryCount" | "createdAt"> {
  const id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `offline-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

  return {
    id,
    payload,
    amountPaid,
    changeDue,
  };
}
